/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

export type MarkdownContentSegment =
  | { type: 'code'; content: string; language?: string }
  | { type: 'text'; content: string; language?: undefined };

export type MarkdownTableSegment =
  | { type: 'text'; content: string }
  | { type: 'table'; header: Array<string>; rows: Array<Array<string>> };

export const MATH_TAG = 'mathblock';

const MARKDOWN_CODE_LANGUAGES = new Set(['markdown', 'md', 'mdx']);

export const isMarkdownCodeLanguage = (language: string | undefined) =>
  MARKDOWN_CODE_LANGUAGES.has((language ?? '').trim().toLowerCase());

const LATEX_CODE_LANGUAGES = new Set(['latex', 'tex', 'katex', 'math']);

export const isLatexCodeLanguage = (language: string | undefined) =>
  LATEX_CODE_LANGUAGES.has((language ?? '').trim().toLowerCase());

export function splitTextIntoSegments(text: string): Array<MarkdownContentSegment> {
  const segments: Array<MarkdownContentSegment> = [];
  const openingFenceRegex = /^```([^\r\n`]*)[^\S\r\n]*(?:\r?\n|$)/gm;
  const closingFenceRegex = /^```\s*(?:\r?\n|$)/gm;
  let lastIndex = 0;
  let match: RegExpExecArray | null = openingFenceRegex.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    closingFenceRegex.lastIndex = openingFenceRegex.lastIndex;
    const closingMatch = closingFenceRegex.exec(text);
    const contentEnd = closingMatch?.index ?? text.length;
    segments.push({
      type: 'code',
      language: languageFromFence(match[1]),
      content: text.slice(openingFenceRegex.lastIndex, contentEnd),
    });
    lastIndex = closingMatch ? closingFenceRegex.lastIndex : text.length;
    openingFenceRegex.lastIndex = lastIndex;
    match = openingFenceRegex.exec(text);
  }

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return segments;
}

const languageFromFence = (fenceMeta: string | undefined) =>
  fenceMeta?.trim().split(/\s+/)[0] || undefined;

export function splitTextIntoMarkdownSegments(text: string): Array<MarkdownTableSegment> {
  const lines = text.split(/\r?\n/);
  const segments: Array<MarkdownTableSegment> = [];
  let textBuffer: Array<string> = [];

  const flushText = () => {
    if (textBuffer.length === 0) return;
    segments.push({ type: 'text', content: textBuffer.join('\n') });
    textBuffer = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const headerLine = lines[index];
    const separatorLine = lines[index + 1];

    if (isMarkdownTableHeader(headerLine, separatorLine)) {
      flushText();
      const header = parseMarkdownTableRow(headerLine);
      const rows: Array<Array<string>> = [];
      index += 2;

      while (index < lines.length && isMarkdownTableRow(lines[index])) {
        rows.push(parseMarkdownTableRow(lines[index]));
        index += 1;
      }

      segments.push({ type: 'table', header, rows });
      index -= 1;
      continue;
    }

    textBuffer.push(headerLine);
  }

  flushText();
  return segments;
}

// Matches LaTeX math the agent emits: \[...\] and $$...$$ (display), \(...\) (inline).
// Single-dollar inline math is deliberately unsupported — plain-text dollar amounts
// ("$100 and $200") would otherwise be swallowed as math.
const MATH_REGEX = /\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|\\\(([\s\S]+?)\\\)/g;

// Replaces each math expression with a single-line placeholder tag carrying the
// URI-encoded TeX source, so markdown parsing (emphasis, tables, paragraphs)
// cannot mangle it. The tag is rendered back to math via a markdown-to-jsx
// override in MarkdownContent.
export function encodeMathAsTags(text: string): string {
  return text.replace(MATH_REGEX, (_match, displayBracket, displayDollar, inline) => {
    const tex = ((displayBracket ?? displayDollar ?? inline) as string).trim();
    const display = inline === undefined;
    return `<${MATH_TAG} data-tex="${encodeURIComponent(tex)}" data-display="${display}"></${MATH_TAG}>`;
  });
}

// markdown-to-jsx 7 treats underscores inside words as emphasis, unlike CommonMark, so
// snake_case names the agent writes ("erosion_control; swppp_design") lose their
// underscores and italicize the text between them. Escape those underscores, leaving
// code spans and fences, indented code blocks (four spaces or a tab after a blank line),
// HTML tags (including the math placeholders), link targets and URLs as-is.
const MARKDOWN_LITERAL_REGEX =
  /(`+)[\s\S]*?\1|(?<=^|\n[ \t]*\n)(?:(?: {4}|\t)[^\n]*(?:\n|$))+|<[^>\n]*>|\]\([^)\s]*\)|(?:https?:\/\/|www\.)[^\s<>()[\]]+/g;
// A whole run, so `Coordinates__Latitude__s` cannot pair its `__` into strong emphasis.
const INTRAWORD_UNDERSCORE_REGEX = /(?<=[\p{L}\p{N}])_+(?=[\p{L}\p{N}])/gu;

export function escapeIntrawordUnderscores(text: string): string {
  const escapeProse = (prose: string) =>
    prose.replace(INTRAWORD_UNDERSCORE_REGEX, (run) => run.replaceAll('_', '\\_'));
  let escaped = '';
  let proseStart = 0;
  for (const literal of text.matchAll(MARKDOWN_LITERAL_REGEX)) {
    escaped += escapeProse(text.slice(proseStart, literal.index)) + literal[0];
    proseStart = literal.index + literal[0].length;
  }
  return escaped + escapeProse(text.slice(proseStart));
}

const LATEX_FENCE_BLOCK_REGEX =
  /^```(?:latex|tex|katex|math)[^\S\r\n]*\r?\n([\s\S]*?)^```[^\S\r\n]*(?:\r?\n|$)/gim;

// Agents that learned to work around missing math support often emit the same
// formula twice in a row — once as a ```latex fence and once as \[...\]/$$...$$.
// Now that both forms render, keep only the first of two adjacent identical
// formulas (separated by whitespace only).
export function removeAdjacentDuplicateMath(text: string): string {
  const occurrences: Array<{ start: number; end: number; tex: string }> = [];
  for (const regex of [LATEX_FENCE_BLOCK_REGEX, MATH_REGEX]) {
    regex.lastIndex = 0;
    let match = regex.exec(text);
    while (match !== null) {
      const tex = match[1] ?? match[2] ?? match[3] ?? '';
      occurrences.push({
        start: match.index,
        end: regex.lastIndex,
        tex: tex.replace(/\s+/g, ''),
      });
      match = regex.exec(text);
    }
  }
  occurrences.sort((a, b) => a.start - b.start);

  const removals: Array<{ start: number; end: number }> = [];
  let previous: { start: number; end: number; tex: string } | undefined;
  for (const occurrence of occurrences) {
    // Skip matches nested inside an earlier one (e.g. \[...\] inside a fence).
    if (previous && occurrence.start < previous.end) continue;
    if (
      previous &&
      occurrence.tex === previous.tex &&
      /^\s*$/.test(text.slice(previous.end, occurrence.start))
    ) {
      removals.push({ start: previous.end, end: occurrence.end });
      continue;
    }
    previous = occurrence;
  }
  if (removals.length === 0) return text;

  let result = '';
  let cursor = 0;
  for (const removal of removals) {
    result += text.slice(cursor, removal.start);
    cursor = removal.end;
  }
  return result + text.slice(cursor);
}

const isMarkdownTableRow = (line: string | undefined) =>
  Boolean(line?.trim()) && (line?.includes('|') ?? false);

const isMarkdownTableSeparator = (line: string | undefined) => {
  if (!isMarkdownTableRow(line)) return false;
  const cells = parseMarkdownTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()));
};

const isMarkdownTableHeader = (headerLine: string | undefined, separatorLine: string | undefined) =>
  isMarkdownTableRow(headerLine) && isMarkdownTableSeparator(separatorLine);

const parseMarkdownTableRow = (line: string | undefined) => {
  const trimmed = line?.trim() ?? '';
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return withoutOuterPipes.split('|').map((cell) => cell.trim());
};

export interface MarkdownPipeTableSource {
  header: Array<string>;
  rows: Array<Array<string>>;
}

export interface MarkdownParts {
  code: Array<{ content: string; language: string }>;
  markdown: Array<string>;
  tables: Array<MarkdownPipeTableSource>;
}

/**
 * The expandable parts of a message in render order, parsed exactly as `MarkdownContent` renders
 * them, so the nth part here is the nth expand button there.
 */
export function getMarkdownParts(text: string): MarkdownParts {
  const parts: MarkdownParts = { code: [], markdown: [], tables: [] };
  for (const segment of splitTextIntoSegments(removeAdjacentDuplicateMath(text))) {
    if (segment.type === 'code') {
      if (isMarkdownCodeLanguage(segment.language)) parts.markdown.push(segment.content);
      else if (!isLatexCodeLanguage(segment.language))
        parts.code.push({ content: segment.content, language: segment.language ?? 'python' });
      continue;
    }
    for (const tableSegment of splitTextIntoMarkdownSegments(encodeMathAsTags(segment.content))) {
      if (tableSegment.type === 'table')
        parts.tables.push({ header: tableSegment.header, rows: tableSegment.rows });
    }
  }
  return parts;
}

export const stableStringHash = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};
